import { useRollbar } from '@rollbar/react'
import axios from 'axios'
import moment from 'moment'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import api from 'services/axiosConfig/api'
import apiReport from 'services/axiosConfig/apiReport'
import unauthenticatedApi from 'services/axiosConfig/unauthenticatedApi'
import Constant from 'utils/Constants'
import LocalStorageUtils from 'utils/LocalStorage'
import { dismissAllToast, errorToast } from 'utils/Toast'

export const BASE_API_CONSTANT = {
  METHOD: {
    POST: 'POST',
    PUT: 'PUT',
    GET: 'GET',
    DELETE: 'DELETE'
  }
}

const useBaseApiHook = (initialLoading = false, showError = true) => {
  const [isLoading, setIsLoading] = useState(initialLoading)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  const { t } = useTranslation()
  const rollbar = useRollbar()

  const executeApi = async (url, method, data, axiosOptions = {}) => {
    setIsLoading(true)

    const { isUnauthenticated = false } = axiosOptions
    const _timestamp = moment(moment().format('MM-DD-YYYY HH:mm:00'), 'MM-DD-YYYY HH:mm:ss').format('x')
    const _url = url.indexOf('?') > 0 ? `${url}&timestamp=${_timestamp}` : `${url}?timestamp=${_timestamp}`

    switch (method) {
      case BASE_API_CONSTANT.METHOD.POST:
        try {
          let response

          if (isUnauthenticated) response = await unauthenticatedApi.post(_url, data, axiosOptions)
          else response = await api.post(_url, data, axiosOptions)

          if (response?.status === 200) {
            setIsLoading(false)
            if (!response?.data?.isSuccess) {
              if (showError) {
                dismissAllToast()
                let errMessage = response?.data?.message ? t(response?.data?.message) : `Failed getting data on ${url}`
                if (!response?.data?.message) {
                  rollbar.error(`Error getting data on ${url}`, {
                    response: response
                  })
                }
                errorToast(errMessage)
              }
            }
            setData(response?.data)

            return response?.data
          }

          return false
        } catch (e) {
          setIsLoading(false)
          setData(null)
          dismissAllToast()
          setError(e?.response?.data)

          if (e?.code === 'ERR_NETWORK' || e?.message === 'timeout exceeded') {
            errorToast(t('globalAlert.canNotConnectToServer', { url }))
            rollbar.error(`Can not connect to server in ${url}`, {
              user_token: LocalStorageUtils.jwtToken.get(),
              device: navigator?.userAgentData,
              reason: e?.message,
              response: e?.response
            })

            return {
              isSuccess: false,
              isError: true,
              message: t('globalAlert.canNotConnectToServerUrl'),
              error: e
            }
          }

          if (showError) {
            let serverError = e?.response?.data?.message || e?.response?.data?.error
            let errMessage = serverError ? t(serverError) : `Failed getting data on ${url}`
            if (!serverError) {
              rollbar.error(`Error getting data on ${url}`, {
                user_token: LocalStorageUtils.jwtToken.get(),
                response: e?.response
              })
            }
            errorToast(errMessage)
          }
          return false
        }

      case BASE_API_CONSTANT.METHOD.DELETE:
        try {
          let response

          if (isUnauthenticated) response = await unauthenticatedApi.delete(url, data, axiosOptions)
          else response = await api.delete(url, data, axiosOptions)

          if (response?.status === 200) {
            setIsLoading(false)
            if (!response?.data?.isSuccess) {
              // errorToast(response?.data || "Request Failed")
            }
            setData(response?.data)

            return response?.data
          }

          return false
        } catch (e) {
          setIsLoading(false)
          setData(null)
          dismissAllToast()

          let serverError = e?.response?.data?.message || e?.response?.data?.error
          let errMessage = serverError ? t(serverError) : `Failed getting data on ${url}`
          errorToast(errMessage)
          setError(e?.response?.data)
          return false
        }

      default:
        try {
          let response

          if (isUnauthenticated) response = await unauthenticatedApi.get(_url, axiosOptions)
          else response = await api.get(_url, axiosOptions)

          if (response?.status === 200) {
            setIsLoading(false)
            setData(response?.data)
            dismissAllToast()

            if (!response?.data?.isSuccess) {
              let errMessage = response?.data?.message ? t(response?.data?.message) : `Failed getting data on ${url}`
              if (!response?.data?.message) {
                rollbar.error(`Error getting data on ${url}`, {
                  response: response
                })
              }
              if (showError) errorToast(errMessage)
            }
            return response?.data
          }

          return false
        } catch (e) {
          setIsLoading(false)
          setData(null)
          dismissAllToast()

          if (e?.code === 'ERR_NETWORK' || e?.message === 'timeout exceeded') {
            errorToast(t('globalAlert.canNotConnectToServer'))
            rollbar.error(`Can not connect to server in ${url}`, {
              user_token: LocalStorageUtils.jwtToken.get(),
              device: navigator?.userAgentData,
              response: e?.response,
              reason: e?.message
            })

            return {
              isSuccess: false,
              isError: true,
              message: t('globalAlert.canNotConnectToServerUrl'),
              error: e
            }
          }

          if (showError) {
            let serverError = e?.response?.data?.message || e?.response?.data?.error
            if (!serverError) {
              rollbar.error(`Error getting data on ${url}`, {
                response: e?.response
              })
            }
            errorToast(serverError ? t(serverError) : e?.message)
          }

          setError(e?.response?.data)
          if (e?.name === 'AxiosError') {
            return {
              isSuccess: false,
              isError: true,
              message: e?.message,
              error: e
            }
          }
          return false
        }
    }
  }

  const executeReportApi = async (url, method, data, axiosOptions = {}) => {
    setIsLoading(true)

    const { isUnauthenticated = false } = axiosOptions

    switch (method) {
      case BASE_API_CONSTANT.METHOD.POST:
        try {
          let response

          if (isUnauthenticated) response = await unauthenticatedApi.post(url, data, axiosOptions)
          else response = await apiReport.post(url, data, axiosOptions)

          if (response?.status === 200) {
            setIsLoading(false)
            if (response?.data?.error) {
              let msg = response?.data?.message || 'Request Failed'
              errorToast(t(msg))
            }

            setData(response?.data)

            return response?.data
          }

          return false
        } catch (e) {
          setIsLoading(false)
          setData(null)
          setError(e?.response?.data)
          let msg = e?.response?.data?.message || e?.response?.data?.error || 'Request Failed'
          errorToast(t(msg))
          return false
        }

      case BASE_API_CONSTANT.METHOD.DELETE:
        try {
          let response

          if (isUnauthenticated) response = await unauthenticatedApi.delete(url, data, axiosOptions)
          else response = await apiReport.delete(url, data, axiosOptions)

          if (response?.status === 200) {
            setIsLoading(false)
            if (response?.data?.error) {
              // errorToast(response?.data || "Request Failed")
            }
            setData(response?.data)

            return response?.data
          }

          return false
        } catch (e) {
          setIsLoading(false)
          setData(null)
          errorToast(e?.response?.data?.message || e?.response?.data?.error || 'Request Failed')
          setError(e?.response?.data)
          return false
        }

      default:
        try {
          let response

          if (isUnauthenticated) response = await unauthenticatedApi.get(url, axiosOptions)
          else response = await apiReport.get(url, axiosOptions)

          if (response?.status === 200) {
            setIsLoading(false)
            setData(response?.data)
            if (response?.data?.error) {
              let msg = response?.data?.message || 'Request Failed'
              errorToast(t(msg))
            }
            return response?.data
          }

          return false
        } catch (e) {
          setIsLoading(false)
          setData(null)
          let msg = e?.response?.data?.message || e?.response?.data?.error || 'Request Failed'
          errorToast(t(msg))
          setError(e?.response?.data)
          return false
        }
    }
  }

  const uploadSingleFile = async (url, formData) => {
    setIsLoading(true)

    const token = LocalStorageUtils.jwtToken.get()

    const uploadApi = axios.create({
      baseURL: Constant.baseApiURL,
      timeout: Constant.apiTimeout,
      headers: {
        Accept: '*/*',
        'Content-Type': 'multipart/form-data',
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${token}`
      },
      data
    })

    try {
      let response = await uploadApi.post(url, formData)
      if (response?.status === 200) {
        setIsLoading(false)
        setData(response?.data)
        if (!response?.data?.isSuccess) {
          dismissAllToast()
          errorToast(response?.data?.message || 'Request Failed')
        }
        return response?.data
      }

      return false
    } catch (e) {
      setIsLoading(false)
      setData(null)
      dismissAllToast()
      errorToast(e?.response?.data?.message || e?.response?.data?.error || 'Request Failed')
      setError(e?.response?.data)
      return e?.response?.data || false
    }
  }

  const uploadMultipleFile = async (url, formData) => {
    setIsLoading(true)

    const token = LocalStorageUtils.jwtToken.get()

    const uploadApi = axios.create({
      baseURL: Constant.baseApiURL,
      method: 'post',
      timeout: Constant.apiTimeout,
      headers: {
        Accept: '*/*',
        'Content-Type': 'multipart/form-data',
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${token}`
      },
      data
    })

    try {
      let response = await uploadApi.post(url, formData)
      if (response?.status === 200) {
        setIsLoading(false)
        setData(response?.data)
        if (!response?.data?.isSuccess) {
          dismissAllToast()
          errorToast(response?.data?.message || 'Request Failed')
        }
        return response?.data
      }

      return false
    } catch (e) {
      setIsLoading(false)
      setData(null)
      dismissAllToast()
      errorToast(e?.response?.data?.message || e?.response?.data?.error || 'Request Failed')
      setError(e?.response?.data)
      return e?.response?.data || false
    }
  }

  const handleBlobResponse = blobFileData => {
    // debugger
    const url = window.URL.createObjectURL(new Blob([blobFileData?.data]))
    const link = document.createElement('a')
    const filename = blobFileData?.headers?.['content-disposition']?.split('filename=')?.[1]
    link.href = url
    link.setAttribute('download', filename) // nama file dan extension sesuaikan dengan file yang di download
    document.body.appendChild(link)
    link.click()
  }

  const download = async (url, isAuthenticated = false, returnRaw = false) => {
    setIsLoading(true)

    let config = {
      responseType: 'blob',
      headers: {
        'Access-Control-Allow-Origin': '*'
      }
    }

    if (isAuthenticated) {
      const tokenBeforeRequest = LocalStorageUtils.jwtToken.get()
      config.headers['Authorization'] = `Bearer ${tokenBeforeRequest}`
    }

    const downloadApi = axios.create(config)

    try {
      let response = await downloadApi.get(url)
      if (response?.status === 200) {
        setIsLoading(false)

        if (returnRaw) {
          return response
        }

        handleBlobResponse(response)
        return true
      }

      return false
    } catch (e) {
      setIsLoading(false)
      errorToast(e?.response?.data?.message || e?.response?.data?.error || 'Request Failed')
      setError(e?.response?.data)
      return false
    }
  }

  return {
    isLoading,
    data,
    error,
    executeApi,
    executeReportApi,
    uploadSingleFile,
    download,
    uploadMultipleFile
  }
}

export default useBaseApiHook
